import type { BlissBasicModelId, BlissBasicTimeline } from './blissBasic'

/** Includes legacy Custom colors so saved plans retain their original selections. */
export type BlissBasicFinishId = 'gloss_white' | 'matte_white' | 'white' | 'black' | 'off_white' | 'espresso' | 'navy' | 'natural_maple' | 'sand' | 'taupe' | 'warm_gray' | 'sage' | 'olive' | 'forest' | 'terracotta' | 'clay' | 'slate_blue' | 'charcoal' | 'white_oak' | 'walnut' | 'red' | 'blue'
export type BlissBasicFinishTreatment = 'painted_stained' | 'unfinished_raw'
export type BlissBasicAccessId = 'center_stairs' | 'side_ladder' | 'corner_stairs' | 'not_sure'
export type BlissBasicStorageId = 'under_bed_drawers' | 'stair_drawers' | 'open_cubbies'

export type BlissBasicConfiguratorStepId =
  | 'layout'
  | 'finish'
  | 'access'
  | 'storage'
  | 'sleepers'
  | 'timeline'
  | 'contact'
  | 'summary'

export type BlissBasicConfiguratorSelections = {
  modelId: BlissBasicModelId
  /** Set on package step: painted/stained vs unfinished raw. */
  finishTreatment?: BlissBasicFinishTreatment
  finishId: BlissBasicFinishId
  accessId: BlissBasicAccessId
  storageIds: BlissBasicStorageId[]
  sleeperCount: number
  timeline: BlissBasicTimeline
  firstName: string
  lastName: string
  email: string
  phone: string
  projectZip: string
  notes: string
  linkedQuoteRequestId?: string
}

export const BLISS_BASIC_FINISH_OPTIONS: ReadonlyArray<{
  id: BlissBasicFinishId
  label: string
  hint: string
}> = [
  { id: 'gloss_white', label: 'Gloss White', hint: 'Bright, reflective finish' },
  { id: 'matte_white', label: 'Matte White', hint: 'Soft, low-sheen finish' },
]

/** Preserved for Custom plans, rendering and readable historical summaries. */
export const BLISS_CUSTOM_FINISH_OPTIONS: typeof BLISS_BASIC_FINISH_OPTIONS = [
  {id:'red',label:'Red',hint:'Custom paint color'},
  {id:'blue',label:'Blue',hint:'Custom paint color'},
  { id: 'white', label: 'White (sheen not specified)', hint: 'Saved finish — choose gloss or matte' },
  { id: 'off_white', label: 'Off-white', hint: 'Custom paint color' },
  { id: 'black', label: 'Black', hint: 'Custom paint color' },
  { id: 'espresso', label: 'Espresso', hint: 'Rich contrast, brass-friendly' },
  { id: 'navy', label: 'Navy accent', hint: 'Bold walls, cream trim' },
  { id: 'natural_maple', label: 'Natural maple', hint: 'Warm wood tone' },
  { id: 'sand', label: 'Sand', hint: 'Custom paint color' },
  { id: 'taupe', label: 'Taupe', hint: 'Custom paint color' },
  { id: 'warm_gray', label: 'Warm gray', hint: 'Custom paint color' },
  { id: 'sage', label: 'Sage', hint: 'Custom paint color' },
  { id: 'olive', label: 'Olive', hint: 'Custom paint color' },
  { id: 'forest', label: 'Forest green', hint: 'Custom paint color' },
  { id: 'terracotta', label: 'Terracotta', hint: 'Custom paint color' },
  { id: 'clay', label: 'Clay', hint: 'Custom paint color' },
  { id: 'slate_blue', label: 'Slate blue', hint: 'Custom paint color' },
  { id: 'charcoal', label: 'Charcoal', hint: 'Custom paint color' },
  { id: 'white_oak', label: 'Natural white oak', hint: 'Natural wood finish' },
  { id: 'walnut', label: 'Walnut', hint: 'Natural wood finish' },
]

export const BLISS_ALL_FINISH_OPTIONS = [...BLISS_BASIC_FINISH_OPTIONS, ...BLISS_CUSTOM_FINISH_OPTIONS]

export function isBasicPaintColor(id: unknown): boolean {
  return BLISS_BASIC_FINISH_OPTIONS.some(option => option.id === id)
}

export const BLISS_BASIC_ACCESS_OPTIONS: ReadonlyArray<{
  id: BlissBasicAccessId
  label: string
  hint: string
}> = [
  { id: 'center_stairs', label: 'Center stairs', hint: 'Built-in steps between bunks' },
  { id: 'corner_stairs', label: 'Corner stairs', hint: 'L-layout foot traffic' },
  { id: 'side_ladder', label: 'Side ladder', hint: 'Smallest footprint' },
  { id: 'not_sure', label: 'Recommend for me', hint: 'Bliss Basic recommends from your room' },
]

export const BLISS_BASIC_STORAGE_OPTIONS: ReadonlyArray<{
  id: BlissBasicStorageId
  label: string
  hint: string
}> = [
  { id: 'under_bed_drawers', label: 'Under-bed drawers', hint: 'Hidden clothes & gear' },
  { id: 'stair_drawers', label: 'Stair drawers', hint: 'Steps double as storage' },
  { id: 'open_cubbies', label: 'Open cubbies', hint: 'Books & baskets' },
]

export const BLISS_BASIC_SLEEPER_OPTIONS = [2, 3, 4, 5, 6, 7, 8] as const

export const BLISS_BASIC_CONFIGURATOR_STEPS: ReadonlyArray<{
  id: BlissBasicConfiguratorStepId
  title: string
  subtitle: string
}> = [
  { id: 'layout', title: 'Layout', subtitle: 'Pick a layout by comparing standard mattress footprints' },
  { id: 'finish', title: 'Finish', subtitle: 'Gloss White or Matte White · other shades and stains with Bliss Custom' },
  { id: 'access', title: 'Access', subtitle: 'Stairs or ladder' },
  { id: 'storage', title: 'Storage', subtitle: 'Optional — select any that fit' },
  { id: 'sleepers', title: 'Occupancy', subtitle: 'How many people need to sleep here?' },
  { id: 'timeline', title: 'Timeline', subtitle: 'When are you hoping to install?' },
  { id: 'contact', title: 'Contact', subtitle: 'Where should Bliss Basic follow up?' },
  { id: 'summary', title: 'Summary', subtitle: 'Review & submit your selections' },
]

const STORAGE_KEY = 'bliss_basic_configurator_v1'

export function defaultBlissBasicConfiguratorSelections(
  prefill?: Partial<BlissBasicConfiguratorSelections>,
): BlissBasicConfiguratorSelections {
  return {
    modelId: 'not_sure',
    finishId: 'matte_white',
    accessId: 'not_sure',
    storageIds: [],
    sleeperCount: 4,
    timeline: 'exploring',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    projectZip: '',
    notes: '',
    ...prefill,
  }
}

export function blissBasicConfiguratorNeedsContactStep(
  selections: BlissBasicConfiguratorSelections,
): boolean {
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selections.email.trim())
  const zipOk = /^\d{5}$/.test(selections.projectZip.trim())
  const nameOk = selections.firstName.trim().length > 0
  return !(emailOk && zipOk && nameOk)
}

/** Customer-facing steps only (layout + finish). Access, timeline, contact, and summary were removed from the funnel. */
export function blissBasicConfiguratorFlow(
  selections: BlissBasicConfiguratorSelections,
  options?: { includeContact?: boolean; skipLayout?: boolean; skipFinish?: boolean },
): BlissBasicConfiguratorStepId[] {
  const skipFinish =
    options?.skipFinish !== undefined
      ? options.skipFinish
      : (selections.finishTreatment === 'painted_stained' && isBasicPaintColor(selections.finishId))
  return [
    ...(options?.skipLayout ? [] : (['layout'] as const)),
    ...(skipFinish ? [] : (['finish'] as const)),
  ]
}

export function loadBlissBasicConfiguratorDraft(): BlissBasicConfiguratorSelections | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as BlissBasicConfiguratorSelections
  } catch {
    return null
  }
}

export function saveBlissBasicConfiguratorDraft(selections: BlissBasicConfiguratorSelections): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selections))
  } catch {
    /* ignore quota */
  }
}

export function clearBlissBasicConfiguratorDraft(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(STORAGE_KEY)
}

export function labelForFinish(id: BlissBasicFinishId): string {
  return BLISS_ALL_FINISH_OPTIONS.find((o) => o.id === id)?.label ?? id
}

export function labelForFinishTreatment(treatment: BlissBasicFinishTreatment): string {
  return treatment === 'painted_stained' ? 'Painted' : 'Unfinished / raw'
}

export function labelForAccess(id: BlissBasicAccessId): string {
  return BLISS_BASIC_ACCESS_OPTIONS.find((o) => o.id === id)?.label ?? id
}

export function labelsForStorage(ids: BlissBasicStorageId[]): string {
  if (ids.length === 0) return 'None selected'
  return ids
    .map((id) => BLISS_BASIC_STORAGE_OPTIONS.find((o) => o.id === id)?.label ?? id)
    .join(', ')
}

export function buildBlissBasicConfiguratorSummaryNotes(
  selections: BlissBasicConfiguratorSelections,
  modelLabel: string,
  timelineLabel: string,
): string {
  const finishLine =
    selections.finishTreatment === 'unfinished_raw'
      ? 'Finish: Unfinished / raw (save more)'
      : selections.finishTreatment === 'painted_stained'
        ? `Finish: Painted — ${labelForFinish(selections.finishId)}`
        : `Finish: ${labelForFinish(selections.finishId)}`
  const parts = [
    `Layout: ${modelLabel}`,
    finishLine,
    `Access: ${labelForAccess(selections.accessId)}`,
    `Storage: ${labelsForStorage(selections.storageIds)}`,
    `Occupancy: ${selections.sleeperCount}`,
    `Timeline: ${timelineLabel}`,
  ]
  if (selections.notes.trim()) {
    parts.push(`Notes: ${selections.notes.trim()}`)
  }
  return parts.join(' · ')
}
